import axios from 'axios';
import authStorage from './authStorage';

export const API_BASE_URL =
  import.meta.env?.VITE_API_BASE_URL?.replace(/\/+$/, '') ||
  'http://localhost:3000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

let isRefreshing = false;
let refreshPromise = null;

const refreshAccessToken = async () => {
  if (isRefreshing) {
    return refreshPromise;
  }

  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  isRefreshing = true;
  refreshPromise = axios
    .post(`${API_BASE_URL}/auth/refresh`, { refreshToken })
    .then((response) => {
      const { accessToken, refreshToken: nextRefreshToken, expiresIn } =
        response.data ?? {};
      if (!accessToken || !nextRefreshToken) {
        throw new Error('Invalid token refresh response');
      }
      authStorage.saveTokens({
        accessToken,
        refreshToken: nextRefreshToken,
        expiresIn,
      });
      return accessToken;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

apiClient.interceptors.request.use(
  (config) => {
    const token = authStorage.getAccessToken();
    if (token && !config.headers.Authorization) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    try {
      originalRequest._retry = true;
      await refreshAccessToken();
      const nextToken = authStorage.getAccessToken();
      if (nextToken) {
        originalRequest.headers.Authorization = `Bearer ${nextToken}`;
      }
      return apiClient(originalRequest);
    } catch (refreshError) {
      authStorage.clearTokens();
      return Promise.reject(refreshError);
    }
  },
);

export default apiClient;
